import type { GuideKey } from "./types";

/**
 * Map the shell Help control to the guide that should open.
 *
 * @purpose Dialog-scoped guides (strain-edit, user-edit, …) must win over the
 *   route key while the form is open; otherwise Help re-opens list help that
 *   is gated off and the user cannot recover dismissed auto-help.
 * @scope Pure helper for PageHelpButton and tests.
 */
export function resolveHelpGuideKey(
  pathKey: GuideKey | null,
  activeStack: readonly GuideKey[],
): GuideKey | null {
  return activeStack[activeStack.length - 1] ?? pathKey;
}

export function pushActiveGuide(stack: readonly GuideKey[], guideKey: GuideKey): GuideKey[] {
  return [...stack, guideKey];
}

export function popActiveGuide(stack: readonly GuideKey[], guideKey: GuideKey): GuideKey[] {
  const index = stack.lastIndexOf(guideKey);
  if (index < 0) {
    return [...stack];
  }
  return [...stack.slice(0, index), ...stack.slice(index + 1)];
}

/** Ignore Help clicks for a PageGuide whose surface is not currently shown. */
export function shouldHandleShowGuideEvent(
  eventKey: GuideKey | undefined,
  guideKey: GuideKey,
  when: boolean,
): boolean {
  return when && eventKey === guideKey;
}

/**
 * Persist “don’t show automatically” only on auto-open dismiss.
 * Manual Help must not write that preference (PATTERNS.md page-help UX).
 */
export function shouldPersistGuideDismiss(
  dontShowAgain: boolean,
  openedManually: boolean,
  authenticated: boolean,
): boolean {
  return dontShowAgain && !openedManually && authenticated;
}

export function helpAriaKind(
  pathKey: GuideKey | null,
  activeKey: GuideKey | null,
): "page" | "dialog" {
  if (activeKey && activeKey !== pathKey) {
    return "dialog";
  }
  return "page";
}
